import { parseDateOnly } from '@shared/utils/formatDateTime';
import { api } from '@cwd/lib/main';
import type {
    Culture,
    DimensionReport,
    ODataListResult,
    QuestionFilter,
    QuestionGroupedByResult,
    QuestionSummaryReport,
    SurveyAnswerType,
    SurveyCommunicationIssue,
    SurveyFreeTextRespondent,
    SurveyRespondent,
    SurveyResponseDetail,
    SurveyResponseListItem,
    SurveyStatistics,
    SurveyStatisticsSummary,
} from '../SurveysTypes';

export interface DateRange {
    start: string | null;
    end: string | null;
}

export function buildQueryString(dateRange: DateRange, filters: QuestionFilter[] = [], cultures: string[] = []): string {
    const parts: string[] = [];

    if (dateRange.start) {
        parts.push(`start=${encodeURIComponent(parseDateOnly(dateRange.start).toISOString())}`);
    }
    if (dateRange.end) {
        const end = parseDateOnly(dateRange.end);
        end.setDate(end.getDate() + 1);
        parts.push(`end=${encodeURIComponent(end.toISOString())}`);
    }

    filters.forEach((filter, i) => {
        if (filter.Value) {
            parts.push(`filters[${i}][QuestionId]=${filter.QuestionId}`);
            parts.push(`filters[${i}][Value]=${encodeURIComponent(filter.Value)}`);
        }
        if (filter.QuestionOptionId) {
            parts.push(`filters[${i}][QuestionId]=${filter.QuestionId}`);
            parts.push(`filters[${i}][QuestionOptionId]=${filter.QuestionOptionId}`);
        }
    });

    cultures.forEach((cultureCode, i) => {
        parts.push(`cultures[${i}]=${cultureCode}`);
    });

    return parts.join('&');
}

export async function getSurveyStatisticsSummary(surveyId: string, qs: string): Promise<SurveyStatisticsSummary> {
    return api.get(`/Surveys/${surveyId}/Statistics/Summary${qs ? '?' + qs : ''}`);
}

export async function getSurveyHasDeletedQuestions(surveyId: string): Promise<boolean> {
    return api.get(`/Surveys/${surveyId}/Statistics/HasDeletedQuestions`);
}

export async function getSurveyStatistics(surveyId: string, qs: string, includeDeleted: boolean): Promise<SurveyStatistics> {
    const sep = qs ? '&' : '?';
    const deletedParam = includeDeleted ? `${sep}includeDeleted=true` : '';
    return api.get(`/Surveys/${surveyId}/Statistics${qs ? '?' + qs : ''}${deletedParam}`);
}

export async function getSurveyAnswerTypes(): Promise<SurveyAnswerType[]> {
    return api.get('/Surveys/AnswerTypes');
}

export async function getSurveyDimensionStatistics(surveyId: string, qs: string): Promise<DimensionReport | null> {
    return api.get(`/Surveys/${surveyId}/Statistics/Dimensions${qs ? '?' + qs : ''}`);
}

export async function getQuestionGroupedBy(surveyId: string, questionId: string, groupByQuestionId: string, qs: string): Promise<QuestionGroupedByResult[]> {
    return api.get(`/Surveys/${surveyId}/Statistics/Questions/${questionId}/GroupedBy/${groupByQuestionId}${qs ? '?' + qs : ''}`);
}

export async function getCultures(): Promise<Culture[]> {
    return api.get('/Cultures');
}

export async function getSurveyStatisticsReport(surveyId: string, qs: string, format: 'xlsx' | 'csv', includeDeleted: boolean): Promise<{ Url: string }> {
    const sep = qs ? '&' : '?';
    const deletedParam = includeDeleted ? '&includeDeleted=true' : '';
    return api.get(`/Surveys/${surveyId}/Report${qs ? '?' + qs : ''}${sep}format=${format}${deletedParam}`);
}

export async function getRelatedIssues(surveyId: string): Promise<SurveyCommunicationIssue[]> {
    return api.get(`/Issues/Summary?SurveyId=${surveyId}`);
}

export async function getQuestionSummaries(surveyId: string): Promise<QuestionSummaryReport | null> {
    return api.get(`/Surveys/${surveyId}/QuestionSummaries`);
}

export async function generateQuestionSummariesAsync(surveyId: string, language: string): Promise<void> {
    return api.post(`/Surveys/${surveyId}/GenerateQuestionSummariesAsync?language=${encodeURIComponent(language)}`);
}

function normalizeODataV3<T>(raw: unknown): ODataListResult<T> {
    if (Array.isArray(raw)) return { Count: raw.length, Results: raw as T[] };
    if (raw && typeof raw === 'object' && 'Count' in raw && 'Results' in raw) return raw as ODataListResult<T>;
    return { Count: 0, Results: [] };
}

function pagingParams(qs: string, skip: number, pageSize: number, extra = ''): string {
    const sep = qs ? '&' : '?';
    return `${qs ? '?' + qs : ''}${sep}$inlinecount=allpages&$skip=${skip}&$top=${pageSize}${extra}`;
}

export async function getRespondents(
    surveyId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/Contacts${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export async function getIssueRespondents(
    surveyId: string,
    issueId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/Issues/${issueId}/Respondents${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export async function getIssueNonRespondents(
    surveyId: string,
    issueId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/Issues/${issueId}/NonRespondents${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export async function getNonRespondents(
    surveyId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/Issues/NonRespondents${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export async function getQuestionOptionRespondents(
    surveyId: string,
    questionOptionId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/QuestionOptions/${questionOptionId}/Contacts${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export async function getQuestionRespondents(
    surveyId: string,
    questionId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/Questions/${questionId}/FreeText/Contacts${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export type NpsGroup = 'Detractors' | 'Passives' | 'Promoters';

export async function getNpsGroupRespondents(
    surveyId: string,
    questionId: string,
    npsGroup: NpsGroup,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyRespondent>> {
    const raw = await api.get(`/Surveys/${surveyId}/Statistics/Questions/${questionId}/Nps/${npsGroup}${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export interface RespondentsEndpointOptions {
    issueId?: string | null;
    isNonRespondents?: boolean;
    questionOptionId?: string | null;
    questionId?: string | null;
    npsGroup?: NpsGroup | null;
    npsGroupQuestionId?: string | null;
}

function respondentsPath(surveyId: string, opts: RespondentsEndpointOptions): string {
    if (opts.questionOptionId) return `/Surveys/${surveyId}/Statistics/QuestionOptions/${opts.questionOptionId}/Contacts`;
    if (opts.questionId) return `/Surveys/${surveyId}/Statistics/Questions/${opts.questionId}/FreeText/Contacts`;
    if (opts.npsGroup && opts.npsGroupQuestionId) return `/Surveys/${surveyId}/Statistics/Questions/${opts.npsGroupQuestionId}/Nps/${opts.npsGroup}`;
    if (opts.issueId) return opts.isNonRespondents ? `/Surveys/${surveyId}/Statistics/Issues/${opts.issueId}/NonRespondents` : `/Surveys/${surveyId}/Statistics/Issues/${opts.issueId}/Respondents`;
    return opts.isNonRespondents ? `/Surveys/${surveyId}/Statistics/Issues/NonRespondents` : `/Surveys/${surveyId}/Statistics/Contacts`;
}

function buildRespondentsUrl(surveyId: string, qs: string, extra: string, opts: RespondentsEndpointOptions): string {
    const sep = qs ? '&' : '?';
    return `${respondentsPath(surveyId, opts)}${qs ? '?' + qs : ''}${sep}${extra}`;
}

export async function exportRespondentsExcel(surveyId: string, qs: string, extra: string, opts: RespondentsEndpointOptions): Promise<{ Url: string }> {
    return api.get(`${buildRespondentsUrl(surveyId, qs, extra, opts)}&$format=excel`);
}

export async function exportRespondentsCsv(surveyId: string, qs: string, extra: string, opts: RespondentsEndpointOptions): Promise<{ Url: string }> {
    return api.get(`${buildRespondentsUrl(surveyId, qs, extra, opts)}&$format=csv`);
}

export async function exportRespondentsToNewList(surveyId: string, qs: string, extra: string, opts: RespondentsEndpointOptions): Promise<{ ListId: string }> {
    return api.get(`${buildRespondentsUrl(surveyId, qs, extra, opts)}&$format=list`);
}

export async function exportRespondentsToExistingList(
    surveyId: string,
    qs: string,
    extra: string,
    opts: RespondentsEndpointOptions,
    targetListId: string
): Promise<{ ListId: string }> {
    return api.get(`${buildRespondentsUrl(surveyId, qs, extra, opts)}&$format=addtolist&targetListId=${targetListId}`);
}

export async function requestRespondentsExcelByMail(surveyId: string, qs: string, extra: string, opts: RespondentsEndpointOptions): Promise<void> {
    return api.get(`${buildRespondentsUrl(surveyId, qs, extra, opts)}&$format=excelmail`);
}

export async function getResponses(
    surveyId: string,
    qs: string,
    skip: number,
    pageSize: number,
    extra = ''
): Promise<ODataListResult<SurveyResponseListItem>> {
    const raw = await api.get(`/Surveys/${surveyId}/Responses${pagingParams(qs, skip, pageSize, extra)}`);
    return normalizeODataV3(raw);
}

export async function getFreeTextAnswers(
    surveyId: string,
    questionId: string,
    qs: string,
    skip: number,
    pageSize: number
): Promise<ODataListResult<SurveyFreeTextRespondent>> {
    const raw = await api.get(
        `/Surveys/${surveyId}/Statistics/Questions/${questionId}/FreeText/Contacts${pagingParams(qs, skip, pageSize, '&$orderby=FirstResponded+desc')}`
    );
    return normalizeODataV3(raw);
}

export async function deleteResponses(surveyId: string, responseIds: string[]): Promise<void> {
    const payload = responseIds.map((id) => ({ SurveyResponseId: id }));
    return api.delete(`/Surveys/${surveyId}/Responses`, payload);
}

export async function getContactResponses(
    surveyId: string,
    contactId: string,
    qs: string,
    skip: number,
    pageSize: number
): Promise<ODataListResult<SurveyResponseListItem>> {
    const raw = await api.get(`/Surveys/${surveyId}/Contacts/${contactId}/Responses${pagingParams(qs, skip, pageSize)}`);
    return normalizeODataV3(raw);
}

export async function getResponse(surveyId: string, surveyResponseId: string): Promise<SurveyResponseDetail> {
    return api.get(`/Surveys/${surveyId}/Responses/${surveyResponseId}`);
}

async function downloadBlob(url: string, fileName: string): Promise<void> {
    const authOptions = api.beforeSend(api.settings);
    const response = await fetch(`${api.settings.apiHost || ''}${url}`, authOptions);
    const blob = await response.blob();

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
}

export async function downloadSurveyResource(resourceId: string, fileName: string): Promise<void> {
    return downloadBlob(`/Resources/ResourceAsFile/${resourceId}?fileName=${encodeURIComponent(fileName)}`, fileName);
}

export async function downloadAllSurveyResources(surveyResponseId: string): Promise<void> {
    return downloadBlob(`/Surveys/Resources/${surveyResponseId}`, `${surveyResponseId}.zip`);
}

export async function downloadSurveyQuestionResources(surveyResponseId: string, questionId: string): Promise<void> {
    return downloadBlob(`/Surveys/Resources/${surveyResponseId}/${questionId}`, `${questionId}.zip`);
}

export async function downloadAllQuestionResources(questionId: string): Promise<void> {
    return downloadBlob(`/Surveys/Resources/Question/${questionId}`, `${questionId}.zip`);
}

export async function getSurveyResourcePreviewUrl(resourceId: string, fileName: string): Promise<string> {
    const authOptions = api.beforeSend(api.settings);
    const url = `${api.settings.apiHost || ''}/Resources/ResourceAsFile/${resourceId}?fileName=${encodeURIComponent(fileName)}`;
    const response = await fetch(url, authOptions);
    const blob = await response.blob();
    return window.URL.createObjectURL(blob);
}
